import type { CursorData, DocumentSection_Attachments, DocumentTemplate } from '@alpod-ai-writer/shared';
import { DocumentSectionType } from '@alpod-ai-writer/shared';
import type { TElement } from '@udecode/plate';
import clsx from 'clsx';
import { ToggleSwitch } from 'flowbite-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Y from 'yjs';

import { Markdown } from '../../components/markdown/markdown.component';
import { useDocumentEditor } from '../hooks/useDocumentEditor.hook';
import { AttachmentsAddButton, AttachmentsEditor } from './attachmentsEditor.component';
import { OtherAttachmentsAddButton, OtherAttachmentsEditor } from './otherAttachmentsEditor.component';
import { CitationListEditor } from './citationListEditor.component';
import { FieldEditor } from './fieldEditor.component';
import { FigureListEditor } from './figureListEditor.component';
import { ShortcutListEditor } from './shortcutListEditor.component';
import { TextEditor } from './textEditor.component';
import { ToCEditor } from './tocEditor.component';

function useAttachmentSectionTitles(yDoc: Y.Doc, section: DocumentSection_Attachments): string[] {
    const arr = yDoc.getArray<Y.Map<string>>('att_items_' + section.contentId);

    const getIds = () => arr.toArray().map((m) => m.get('id') ?? '').filter(Boolean);
    const getTitles = (ids: string[]) => ids.map((id) => yDoc.getText('att_title_' + id).toString());

    const [ids, setIds] = useState<string[]>(getIds);
    const [titles, setTitles] = useState<string[]>(() => getTitles(getIds()));

    useEffect(() => {
        const handler = () => {
            const nextIds = getIds();
            setIds(nextIds);
            setTitles(getTitles(nextIds));
        };
        arr.observe(handler);
        return () => arr.unobserve(handler);
    }, [arr]);

    useEffect(() => {
        if (ids.length === 0) return;
        const textNodes = ids.map((id) => yDoc.getText('att_title_' + id));
        const handler = () => setTitles(ids.map((id) => yDoc.getText('att_title_' + id).toString()));
        textNodes.forEach((t) => t.observe(handler));
        return () => textNodes.forEach((t) => t.unobserve(handler));
    }, [ids]);

    return titles;
}

function AttachmentSectionPreview({ yDoc, section }: { yDoc: Y.Doc; section: DocumentSection_Attachments }) {
    const titles = useAttachmentSectionTitles(yDoc, section);

    if (titles.length === 0) return null;

    return (
        <>
            {titles.map((title, idx) => (
                <li key={idx} className="text-sm text-gray-700">
                    <span className="text-gray-400 mr-2 tabular-nums">{idx + 1}.</span>
                    {title || <span className="text-gray-400 italic">Bez názvu</span>}
                </li>
            ))}
        </>
    );
}

function AttachmentListPreview({ yDoc, template }: { yDoc: Y.Doc; template: DocumentTemplate }) {
    const attachmentSections = template.config.sections.filter(
        (s): s is DocumentSection_Attachments => s.type === DocumentSectionType.Attachments
    );

    if (attachmentSections.length === 0) {
        return (
            <p className="px-4 md:px-8 text-sm text-gray-500 italic">
                Finální podoba seznamu příloh bude provedena při exportu dokumentu.
            </p>
        );
    }

    return (
        <div className="px-4 md:px-8 flex flex-col gap-3">
            <p className="text-sm text-gray-500 italic">Finální podoba bude provedena při exportu. Přílohy:</p>
            <ol className="flex flex-col gap-1 list-none">
                {attachmentSections.map((s) => (
                    <AttachmentSectionPreview key={s.contentId} yDoc={yDoc} section={s} />
                ))}
            </ol>
        </div>
    );
}

export interface DocumentSectionProps {
    template: DocumentTemplate;
    yDoc: Y.Doc;
    currentSection: string;
    cursorData: CursorData;
    toolbarEl?: HTMLDivElement|null;
    readOnly?: boolean;
    className?: string;
    revisionContent?: Record<string, TElement> | null;
}

export const DocumentSectionWrapper: React.FC<DocumentSectionProps> = (props) => {
    const docEditor = useDocumentEditor();
    const wasInitialized = useRef(false);
    const [ isInitialized, setIsInitialized ] = useState(true);

    useEffect(() => {
        if (!wasInitialized.current) {
            wasInitialized.current = true;
            return;
        }

        setIsInitialized(false);

        const t = setTimeout(() => {
            setIsInitialized(true);
        }, 100);

        return () => {
            clearTimeout(t);
        };
    }, [ docEditor.currentRevisionId ]);

    if (!isInitialized) {
        return null;
    }

    return (
        <DocumentSection {...props} />
    );
};

export const DocumentSection: React.FC<DocumentSectionProps> = ({
    template,
    yDoc,
    currentSection,
    cursorData,
    readOnly,
    toolbarEl,
    className,
    revisionContent,
}) => {
    const section = template.config.sections.find((section) => section.id === currentSection);

    const { documentFieldList } = useMemo(() => {
        const contentSections = template.config.sections.filter((section) => section.type === DocumentSectionType.Content);
        const documentFieldList = contentSections.map((section) => section.content?.id ?? '').filter((id) => id !== '');
        return { documentFieldList };
    }, [ template.config.sections ]);

    const isFullyOptional = (
        section?.type === DocumentSectionType.OtherAttachments
    ) || (
        section?.type === DocumentSectionType.Content &&
        section.fields.length === 0 &&
        !!section.content &&
        !section.content.isRequired
    );

    const [isOptedOut, setIsOptedOut] = useState(() => {
        if (!isFullyOptional || !section) return false;
        return yDoc.getMap<boolean>('sectionOptOut').get(section.id) === true;
    });

    useEffect(() => {
        if (!isFullyOptional || !section) {
            setIsOptedOut(false);
            return;
        }
        const map = yDoc.getMap<boolean>('sectionOptOut');
        setIsOptedOut(map.get(section.id) === true);
        const handler = () => setIsOptedOut(map.get(section.id) === true);
        map.observe(handler);
        return () => map.unobserve(handler);
    }, [yDoc, section?.id, isFullyOptional]);

    const handleOptOutToggle = useCallback((hasContent: boolean) => {
        if (!section) return;
        const map = yDoc.getMap<boolean>('sectionOptOut');
        if (!hasContent) {
            map.set(section.id, true);
        } else {
            map.delete(section.id);
        }
    }, [yDoc, section?.id]);

    if (!section) {
        return null;
    }

    return (
        <div className={clsx(
            'flex flex-col gap-6',
            className
        )}>
            <div className='flex flex-col gap-2 px-4 md:px-8'>
                <div className='flex items-center justify-between gap-3'>
                    <h1 className='text-2xl md:text-3xl font-medium text-[#0b111e]'>{section.title}</h1>
                    {section.type === DocumentSectionType.Attachments && !readOnly && (
                        <AttachmentsAddButton yDoc={yDoc} contentId={section.contentId} />
                    )}
                    {section.type === DocumentSectionType.OtherAttachments && !readOnly && !isOptedOut && (
                        <OtherAttachmentsAddButton yDoc={yDoc} contentId={section.contentId} />
                    )}
                </div>

                <Markdown
                    source={section.instructions}
                    className='markdown text-sm text-gray-700'
                />

                {section.type === DocumentSectionType.ToC && (
                    <p className='text-sm text-gray-700'>Finální podoba obsahu bude provedena u exportu dokumentu.</p>
                )}
            </div>

            {isFullyOptional && (
                <div className='flex flex-row items-center justify-between gap-3 px-4 md:px-8'>
                    <span className='text-sm font-medium text-gray-700'>Je táto sekce součastí práce?</span>
                    <ToggleSwitch
                        checked={!isOptedOut}
                        onChange={handleOptOutToggle}
                        color='purple'
                        theme={{
                            toggle: {
                                checked: {
                                    color: {
                                        purple: 'border-primary-500 bg-primary-500'
                                    }
                                }
                            }
                        }}
                        disabled={readOnly}
                    />
                </div>
            )}

            {isOptedOut ? (
                <p className='px-4 md:px-8 text-sm text-gray-500 italic'>
                    Sekce je označena jako bez obsahu.
                </p>
            ) : (
                <>
                    <div className='flex flex-col gap-6 px-4 md:px-8 empty:hidden'>
                        {section.type === DocumentSectionType.Content && section.fields.map((field) => (
                            <FieldEditor
                                key={field.id}
                                yDoc={yDoc}
                                field={field}
                                readOnly={readOnly ?? false}
                            />
                        ))}
                    </div>

                    {section.type === DocumentSectionType.Content && section.content?.id && (
                        <TextEditor
                            documentField={section.content.id}
                            documentFieldList={documentFieldList}
                            cursorData={cursorData}
                            yjsNode={yDoc.get(section.content.id, Y.XmlText)}
                            displayWordCount={section.content.displayWordCount}
                            className='relative w-full p-4 lg:p-8 bg-white rounded-md'
                            toolbarEl={toolbarEl}
                            preloadedContent={revisionContent?.[section.content.id] ?? null}
                        />
                    )}
                </>
            )}

            {section.type === DocumentSectionType.Attachments && (
                <AttachmentsEditor
                    yDoc={yDoc}
                    contentId={section.contentId}
                    cursorData={cursorData}
                    toolbarEl={toolbarEl}
                    readOnly={readOnly ?? false}
                />
            )}

            {section.type === DocumentSectionType.ToC && (
                <ToCEditor />
            )}

            {section.type === DocumentSectionType.CitationList && (
                <CitationListEditor
                    yDoc={yDoc}
                    readOnly={readOnly ?? false}
                    citationGroups={section.citationGroups ?? []}
                />
            )}

            {section.type === DocumentSectionType.ShortcutList && (
                <ShortcutListEditor
                    yDoc={yDoc}
                    readOnly={readOnly ?? false}
                />
            )}

            {section.type === DocumentSectionType.FigureList && (
                <>
                    {(section.figureMinCount ?? 1) > 1 && (
                        <p className='px-4 md:px-8 text-sm text-gray-600'>
                            Minimální počet položek pro export:{' '}
                            <strong>{section.figureMinCount} položek</strong>
                        </p>
                    )}
                    <FigureListEditor figureListKind={section.figureListKind} />
                </>
            )}

            {section.type === DocumentSectionType.AttachmentList && (
                <AttachmentListPreview yDoc={yDoc} template={template} />
            )}

            {section.type === DocumentSectionType.OtherAttachments && !isOptedOut && (
                <OtherAttachmentsEditor
                    yDoc={yDoc}
                    contentId={section.contentId}
                    readOnly={readOnly ?? false}
                />
            )}
        </div>
    );
};
